import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Award, Calendar, Code, ExternalLink, Trophy, Target } from "lucide-react";
import { CertificatePreview } from "@/components/practice/CertificatePreview";
import { ChallengeCertificatePreview } from "@/components/challenges/ChallengeCertificatePreview";
import { Helmet } from "react-helmet-async";
import { useNoIndex } from "@/hooks/useCanonicalTag";

const trackCategoryLabels: Record<string, string> = {
  power_bi: "Power BI",
  sql: "SQL",
  python: "Python",
  statistics: "Estatística Aplicada",
  google_analytics: "Google Analytics",
  google_tag_manager: "Google Tag Manager",
  looker_studio: "Looker Studio",
  data_engineering: "Engenharia de Dados",
  machine_learning: "Machine Learning",
  excel_advanced: "Excel Avançado",
  cloud_aws: "AWS para Dados",
  cloud_azure: "Azure para Dados",
  cloud_gcp: "GCP para Dados",
};

const challengeCategoryLabels: Record<string, string> = {
  python: "Python",
  sql: "SQL",
  dax: "DAX",
};

interface TrackCert {
  id: string;
  category: string;
  certificate_number: string;
  issued_at: string;
  total_questions_answered: number;
  accuracy_percentage: number;
  is_valid: boolean;
}

interface ChallengeCert {
  id: string;
  category: string;
  certificate_number: string;
  issued_at: string;
  total_challenges: number;
  completed_challenges: number;
  accuracy_percentage: number;
  is_valid: boolean;
}

export default function Certificates() {
  useNoIndex();
  const user = useUser();
  const [selectedTrack, setSelectedTrack] = useState<{
    cert: TrackCert;
    title: string;
    hours: number;
  } | null>(null);
  const [selectedChallenge, setSelectedChallenge] = useState<{
    cert: ChallengeCert;
    title: string;
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["my-certificates", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return { tracks: [], challenges: [], userName: "Usuário", requirements: [] };

      const [tracksRes, challengesRes, profileRes, reqRes] = await Promise.all([
        supabase
          .from("user_certificates")
          .select("*")
          .eq("user_id", user.id)
          .order("issued_at", { ascending: false }),
        supabase
          .from("challenge_certificates")
          .select("*")
          .eq("user_id", user.id)
          .order("issued_at", { ascending: false }),
        supabase.from("profiles").select("full_name").eq("id", user.id).single(),
        supabase.from("certificate_requirements").select("category, certificate_title, estimated_hours"),
      ]);

      return {
        tracks: (tracksRes.data || []) as TrackCert[],
        challenges: (challengesRes.data || []) as ChallengeCert[],
        userName: profileRes.data?.full_name || "Usuário",
        requirements: reqRes.data || [],
      };
    },
  });

  const userName = data?.userName || "Usuário";
  const tracks = data?.tracks || [];
  const challenges = data?.challenges || [];
  const requirements = data?.requirements || [];

  const getTrackTitle = (category: string) => {
    const req = requirements.find((r: any) => r.category === category);
    return req?.certificate_title || trackCategoryLabels[category] || category;
  };

  const getTrackHours = (category: string) => {
    const req = requirements.find((r: any) => r.category === category);
    return req?.estimated_hours || 20;
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  if (isLoading) {
    return (
      <CandidateLayout maxWidth="6xl">
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        </div>
      </CandidateLayout>
    );
  }

  const hasAny = tracks.length > 0 || challenges.length > 0;

  return (
    <CandidateLayout maxWidth="6xl">
      <Helmet>
        <title>Meus Certificados | Data Hire Club</title>
      </Helmet>

      <div className="space-y-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Award className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold">Meus Certificados</h1>
          </div>
          <p className="text-muted-foreground">
            Todos os certificados conquistados na plataforma. Compartilhe e ganhe pontos no ranking!
          </p>
        </div>

        {!hasAny && (
          <Card>
            <CardContent className="py-16 text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Award className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold">Você ainda não tem certificados</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Complete desafios técnicos ou trilhas de estudo para conquistar seus primeiros certificados.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Link to="/candidate/challenges">
                  <Button>
                    <Code className="h-4 w-4 mr-2" />
                    Começar um desafio
                  </Button>
                </Link>
                <Link to="/candidate/practice">
                  <Button variant="outline">
                    <Target className="h-4 w-4 mr-2" />
                    Ver trilhas
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {tracks.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Certificados de Trilhas ({tracks.length})</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tracks.map((cert) => (
                <Card key={cert.id} className="border-primary/30 bg-primary/5 hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base leading-tight">{getTrackTitle(cert.category)}</CardTitle>
                      {cert.is_valid ? (
                        <Badge variant="default" className="shrink-0">Válido</Badge>
                      ) : (
                        <Badge variant="destructive" className="shrink-0">Revogado</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDate(cert.issued_at)}
                      </p>
                      <p className="font-mono text-xs truncate">{cert.certificate_number}</p>
                      <p>
                        Aproveitamento: <span className="font-semibold text-foreground">{cert.accuracy_percentage}%</span>
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        setSelectedTrack({
                          cert,
                          title: getTrackTitle(cert.category),
                          hours: getTrackHours(cert.category),
                        })
                      }
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Visualizar
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {challenges.length > 0 && (
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <Code className="h-5 w-5 text-emerald-500" />
              <h2 className="text-xl font-semibold">Certificados de Desafios Técnicos ({challenges.length})</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {challenges.map((cert) => (
                <Card
                  key={cert.id}
                  className="border-emerald-500/30 bg-emerald-500/5 hover:shadow-md transition-shadow"
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base leading-tight">
                        {challengeCategoryLabels[cert.category] || cert.category}
                      </CardTitle>
                      {cert.is_valid ? (
                        <Badge className="shrink-0 bg-emerald-500 hover:bg-emerald-500/90">Válido</Badge>
                      ) : (
                        <Badge variant="destructive" className="shrink-0">Revogado</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDate(cert.issued_at)}
                      </p>
                      <p className="font-mono text-xs truncate">{cert.certificate_number}</p>
                      <p>
                        {cert.completed_challenges}/{cert.total_challenges} desafios •{" "}
                        <span className="font-semibold text-foreground">{cert.accuracy_percentage}%</span>
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        setSelectedChallenge({
                          cert,
                          title: challengeCategoryLabels[cert.category] || cert.category,
                        })
                      }
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Visualizar
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>

      {selectedTrack && (
        <CertificatePreview
          certificate={selectedTrack.cert}
          userName={userName}
          certificateTitle={selectedTrack.title}
          estimatedHours={selectedTrack.hours}
          onClose={() => setSelectedTrack(null)}
        />
      )}

      {selectedChallenge && (
        <ChallengeCertificatePreview
          certificate={selectedChallenge.cert}
          userName={userName}
          certificateTitle={selectedChallenge.title}
          onClose={() => setSelectedChallenge(null)}
        />
      )}
    </CandidateLayout>
  );
}
